import { MenuListButton, } from '@innovyze/stylovyze/lib/components';
import { TextOverflow } from '@innovyze/stylovyze/lib/components/TextOverflow';
import WithLink from '@innovyze/stylovyze/lib/components/WithLink';
import { rem } from '@innovyze/stylovyze/lib/utils/styles';
import { TableRow, Box } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import TableSortLabel from '@mui/material/TableSortLabel';
import React, { useEffect, useState } from 'react';
import { HeaderCell, StyledTable, StyledTableBody, StyledTableCell, StyledTableHead, TableRowWithStyles, } from './StylovyzeTable.styles';
import { StylovyzeTableBodySkeleton } from './StylovyzeTableBodySkeleton';
import { useConsumeWeaveTheme } from '../../hocs/ApplicationWrapper/theme/WeaveContext';
const getContentAndProps = (cell) => {
    if (cell && typeof cell === 'object' && 'content' in cell) {
        return cell;
    }
    return { content: cell, props: {}, ellipsis: false };
};
const StylovyzeTableBody = ({ headers, initialized, rows: inputRows, dataCy, handleRowClick: handleRowClickProp, rowHeight, minHeight, sort, rowsPerPage, useSkeleton, hasButtonItems, emptyTableBody, checkboxSelector, }) => {
    const checkboxColor = '#007CA0';
    const handleRowClick = handleRowClickProp ||
        ((path, onClick) => () => {
            if (onClick)
                onClick();
        });
    const { resolvedRoles, isWeaveTheme } = useConsumeWeaveTheme();
    const [order, setOrder] = useState(sort?.order || 'asc');
    const [orderBy, setOrderBy] = useState(sort?.name || '');
    const [rows, setRows] = useState([]);
    useEffect(() => {
        setRows(inputRows);
    }, [inputRows]);
    const handleSort = (name) => (event) => {
        const newOrder = orderBy === name && order === 'asc' ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(name);
        sort?.onRequestSort(event, name, newOrder);
    };
    const clickRowEnabled = rows.filter(row => Boolean(row.pathToGoWhenClicked || row.onClick))
        .length > 0;
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledTable, { size: "medium", "data-cy": dataCy },
            React.createElement(StyledTableHead, { headerBackgroundColor: isWeaveTheme &&
                    resolvedRoles['table.header.backgroundColor'], headerTextColor: isWeaveTheme && resolvedRoles['table.header.iconColor'] },
                React.createElement(TableRow, null,
                    checkboxSelector ? (React.createElement(HeaderCell, { "data-cy": "checkbox-header", width: "20px", align: "left", className: "checkbox-container", direction: undefined },
                        React.createElement(Checkbox, { checked: checkboxSelector?.headerCheckboxChecked, onChange: (_, checked) => {
                                checkboxSelector?.onHeaderCheckboxChange?.(checked);
                            }, sx: {
                                '&.Mui-checked': {
                                    color: checkboxColor,
                                },
                            } }))) : null,
                    headers.map(({ key, cell, width, align, sortable, maxWidth, padding, borderLeft, borderRight, }) => {
                        const isOrdered = orderBy === key;
                        const tableSortDirection = isOrdered
                            ? order
                            : 'asc';
                        return (React.createElement(HeaderCell, { "data-cy": `${key}-header`, key: key, width: width, align: align, direction: isOrdered ? order : undefined, style: {
                                maxWidth: maxWidth,
                                padding: padding,
                                borderLeft: borderLeft,
                                borderRight: borderRight,
                            } }, sortable ? (React.createElement(TableSortLabel, { active: isOrdered, direction: tableSortDirection, onClick: handleSort(key) }, cell)) : (cell)));
                    }),
                    (rows?.some(row => row.buttonItems?.length) ||
                        hasButtonItems) && (React.createElement(HeaderCell, { direction: undefined })))),
            initialized && (React.createElement(StyledTableBody, { "$minHeight": minHeight }, rows.map((row, rowIndex) => {
                return (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                React.createElement(TableRowWithStyles, { key: row.key, "data-cy": row.dataCy ||
                        `${dataCy}-row-${rowIndex}`, selected: row.selected, hover: clickRowEnabled, onClick: handleRowClick
                        ? handleRowClick(row.pathToGoWhenClicked, row.onClick)
                        : () => {
                            return;
                        } },
                    checkboxSelector ? (React.createElement(StyledTableCell, { id: "checkbox-container" },
                        React.createElement(Checkbox, { sx: {
                                '&.Mui-checked': {
                                    color: checkboxColor,
                                },
                            }, onChange: (_, checked) => {
                                checkboxSelector?.onRowCheckboxChange?.(row, checked);
                            }, checked: row.selected }))) : null,
                    row.cells
                        .slice(0, headers.length)
                        .map((cell, cellIndex) => {
                        const { content, props, ellipsis, extraContent, wordWrap, } = getContentAndProps(cell);
                        return (React.createElement(StyledTableCell, { key: `${row.key}-cell-${cellIndex}`, "data-cy": headers[cellIndex]
                                .dataCy ||
                                `${dataCy}-row-${rowIndex}-cell-${cellIndex}`, align: headers[cellIndex]
                                .align || 'inherit', width: headers[cellIndex].width, height: rowHeight, style: {
                                maxWidth: headers[cellIndex]
                                    .maxWidth,
                                padding: headers[cellIndex]
                                    .padding,
                                borderLeft: headers[cellIndex]
                                    .borderLeft,
                                borderRight: headers[cellIndex]
                                    .borderRight,
                                ...(wordWrap
                                    ? {
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                    }
                                    : {}),
                            }, ...props },
                            React.createElement(WithLink, { padding: "1rem 1.5rem", margin: "-1rem -1.5rem", to: row.pathToGoWhenClicked, key: `${row.key}-cell` }, ellipsis ? (React.createElement(Box, { sx: {
                                    display: 'flex',
                                    ...(wordWrap
                                        ? {
                                            flexWrap: 'wrap',
                                        }
                                        : {}),
                                } },
                                React.createElement(TextOverflow, null, content),
                                extraContent ??
                                    '')) : (React.createElement(Box, { sx: {
                                    display: 'flex',
                                    ...(wordWrap
                                        ? {
                                            flexWrap: 'wrap',
                                        }
                                        : {}),
                                } },
                                content,
                                extraContent ??
                                    '')))));
                    }),
                    row?.buttonItems?.length && (React.createElement(StyledTableCell, { ...row?.buttonItemProps, key: `${row.key}-cell-more-info`, width: rem(48), height: rowHeight, onClick: e => {
                            e.stopPropagation();
                        } },
                        React.createElement(MenuListButton, { buttonCy: "row-menu", menuItems: row.buttonItems })))));
            }))),
            !initialized && useSkeleton && (React.createElement(StylovyzeTableBodySkeleton, { headers: headers, ...useSkeleton, rowHeight: rowHeight, rowsPerPage: rowsPerPage, hasButtonItems: hasButtonItems }))),
        emptyTableBody &&
            initialized &&
            rows.length <= 0 &&
            emptyTableBody));
};
export default StylovyzeTableBody;
